/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface PassdownLogArchivedLogProps {
  id: string;
  passdownLogId: string;
  archivedById: string;
  dateArchived: Date;
  companyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class PassdownLogArchivedLogRecord extends Record<PassdownLogArchivedLogProps>({
  id: null as any,
  passdownLogId: null as any,
  archivedById: null as any,
  dateArchived: null as any,
  companyId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    passdownLogId: string;
    archivedById: string;
    dateArchived: string | number | Date;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): PassdownLogArchivedLogRecord {
    if (json == null) {
      throw new Error('PassdownLogArchivedLogRecord.fromJS: parameter cannot be null');
    }
    return new PassdownLogArchivedLogRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'PassdownLogArchivedLogJson.id',
      ),
      passdownLogId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'passdownLogId', '00000000-0000-0000-0000-000000000000'),
        'PassdownLogArchivedLogJson.passdownLogId',
      ),
      archivedById: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'archivedById', '00000000-0000-0000-0000-000000000000'),
        'PassdownLogArchivedLogJson.archivedById',
      ),
      dateArchived: new Date(
        getFromOrThrow(
          json,
          (j) => propValueOrDefault(j, 'dateArchived', '0001-01-01T00:00:00.00Z'),
          'PassdownLogArchivedLogJson.dateArchived',
        ),
      ),
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'PassdownLogArchivedLogJson.companyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    passdownLogId: string;
    archivedById: string;
    dateArchived: string | number | Date;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [PassdownLogArchivedLogRecord, Error[]] {
    if (json == null) {
      throw new Error('PassdownLogArchivedLogRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new PassdownLogArchivedLogRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'PassdownLogArchivedLogJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      passdownLogId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'passdownLogId', '00000000-0000-0000-0000-000000000000'),
            'PassdownLogArchivedLogJson.passdownLogId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      archivedById: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'archivedById', '00000000-0000-0000-0000-000000000000'),
            'PassdownLogArchivedLogJson.archivedById',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      dateArchived: tryOrDefault(
        () =>
          new Date(
            getFromOrThrow(
              json,
              (j) => propValueOrDefault(j, 'dateArchived', '0001-01-01T00:00:00.00Z'),
              'PassdownLogArchivedLogJson.dateArchived',
            ),
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'PassdownLogArchivedLogJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: PassdownLogArchivedLogProps) {
    super(props);
    if (props == null) {
      throw new Error('PassdownLogArchivedLogRecord constructor parameter cannot be null');
    }
  }
}

export abstract class PassdownLogArchivedLogViewModelBase implements PassdownLogArchivedLogProps {
  protected readonly _props: PassdownLogArchivedLogProps;
  private _json?: PassdownLogArchivedLogProps;

  get id(): string {
    return this._props.id;
  }
  get passdownLogId(): string {
    return this._props.passdownLogId;
  }
  get archivedById(): string {
    return this._props.archivedById;
  }
  get dateArchived(): Date {
    return this._props.dateArchived;
  }
  get companyId(): string {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  constructor(props: PassdownLogArchivedLogProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        passdownLogId: this._props.passdownLogId,
        archivedById: this._props.archivedById,
        dateArchived: this._props.dateArchived,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllPassdownLogArchivedLogEntities = (state: AppState) =>
  state.entityStores.passdownLogArchivedLog.entities;

export const selectPassdownLogArchivedLogById = (id: string) => (state: AppState) =>
  state.entityStores.passdownLogArchivedLog.entities.get(id);

export const selectPassdownLogArchivedLogMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.passdownLogArchivedLog.meta.get(id);

export const selectPassdownLogArchivedLogAndMetadataById = (id: string) =>
  createSelector(
    selectPassdownLogArchivedLogById(id),
    selectPassdownLogArchivedLogMetadataById(id),
    (passdownLogArchivedLog, meta) => ({ passdownLogArchivedLog, meta }),
  );

export function selectPassdownLogArchivedLogEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllPassdownLogArchivedLogEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, PassdownLogArchivedLogRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectPassdownLogArchivedLogEntitiesByPassdownLogId(passdownLogId: string) {
  return createSelector(selectAllPassdownLogArchivedLogEntities, (entities) =>
    entities.filter((e) => e.passdownLogId === passdownLogId).valueSeq(),
  );
}

export function selectPassdownLogArchivedLogEntitiesByArchivedById(archivedById: string) {
  return createSelector(selectAllPassdownLogArchivedLogEntities, (entities) =>
    entities.filter((e) => e.archivedById === archivedById).valueSeq(),
  );
}

export function selectPassdownLogArchivedLogEntitiesByDateArchived(dateArchived: Date) {
  return createSelector(selectAllPassdownLogArchivedLogEntities, (entities) =>
    entities.filter((e) => e.dateArchived === dateArchived).valueSeq(),
  );
}

export function selectPassdownLogArchivedLogEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllPassdownLogArchivedLogEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
